import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { db } from "../utils/firebase";
import { signOut } from "firebase/auth";
import {
  collection,
  query,
  where,
  getDocs,
  writeBatch,
  Timestamp,
} from "firebase/firestore";
import { Bell, LogOut, User } from "lucide-react";
import { Spinner } from "../components/index.js";
import { useAuth } from "../hooks/AuthenticationProvider";

const ResidentNav = () => {
  const {
    user,
    isLoading: loading,
    organizationId,
    organizationData,
    logout, // Use the centralized logout function
  } = useAuth();
  const organizationName = organizationData?.organizationName;
  const navigate = useNavigate();
  const [showDropdown, setShowDropdown] = useState(false);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [notifications, setNotifications] = useState([]); // Store fetched notifications
  const [isNotificationModalOpen, setNotificationModalOpen] = useState(false); // Modal state

  // Fetch unread notifications
  const fetchNotifications = async () => {
    // Check if notifications should be suppressed (for Leaders viewing their own dashboard)
    if (sessionStorage.getItem("suppressNotifications") === "true") {
      console.log(
        "Notifications suppressed for Leader viewing their own dashboard"
      );
      return;
    }

    if (!user || !organizationId) return;

    try {
      // Query the user's notification subcollection
      const notificationsQuery = query(
        collection(
          db,
          `organizations/${organizationId}/users/${user.email}/notifications`
        ),
        where("isRead", "==", false) // Only fetch unread notifications
      );

      const querySnapshot = await getDocs(notificationsQuery);
      const fetchedNotifications = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      console.log(
        `Fetched ${fetchedNotifications.length} unread notifications for resident`
      );
      setNotifications(fetchedNotifications);
      setUnreadNotifications(fetchedNotifications.length);
    } catch (error) {
      console.error("Error fetching notifications:", error);
    }
  };

  // Also update your useEffect to check for the suppression flag
  useEffect(() => {
    // Skip notification fetching if suppression flag is set
    if (sessionStorage.getItem("suppressNotifications") !== "true") {
      fetchNotifications();
    } else {
      console.log("Initial notification fetch suppressed for Leader");
    }
  }, [user, organizationId]);

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error("Error during logout:", error);
    }
  };

  // Delete notifications older than 30 days
  const deleteOldNotifications = async () => {
    if (!organizationId || !user?.email) return;

    const thirtyDaysAgo = Timestamp.fromMillis(
      Date.now() - 30 * 24 * 60 * 60 * 1000
    );

    try {
      const notificationsQuery = query(
        collection(
          db,
          `organizations/${organizationId}/users/${user.email}/notifications`
        ),
        where("date", "<", thirtyDaysAgo) // Find notifications older than 30 days
      );

      const querySnapshot = await getDocs(notificationsQuery);
      const batch = writeBatch(db);

      querySnapshot.forEach((docSnapshot) => {
        batch.delete(docSnapshot.ref);
      });

      if (querySnapshot.docs.length > 0) {
        await batch.commit();
        console.log(`Deleted ${querySnapshot.docs.length} old notifications`);
      }
    } catch (error) {
      console.error("Error deleting old notifications:", error);
    }
  };

  // Mark notifications as read
  const markNotificationsAsRead = async () => {
    if (!user?.email || !organizationId) return;

    try {
      const notificationsQuery = query(
        collection(
          db,
          `organizations/${organizationId}/users/${user.email}/notifications`
        ),
        where("isRead", "==", false)
      );

      const querySnapshot = await getDocs(notificationsQuery);

      if (querySnapshot.empty) {
        console.log("No unread notifications to mark as read");
        return;
      }

      const batch = writeBatch(db);
      querySnapshot.docs.forEach((docSnapshot) => {
        batch.set(docSnapshot.ref, { isRead: true }, { merge: true });
      });

      await batch.commit();
      console.log(`Marked ${querySnapshot.docs.length} notifications as read`);

      setUnreadNotifications(0); // Reset unread notifications count
      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true }))); // Update local state
    } catch (error) {
      console.error("Error marking notifications as read:", error);
    }
  };

  // Fetch notifications and delete old ones on mount
  useEffect(() => {
    fetchNotifications();
    deleteOldNotifications();
  }, [user, organizationId]);

  if (loading) {
    return <Spinner />;
  }

  return (
    <nav className="fixed top-0 left-0 right-0 h-16 z-50 border-b border-slate-800/30 bg-[#0A0F1E]/95 backdrop-blur-xl">
      <div className="h-full px-6 mx-auto flex items-center justify-between">
        {/* Logo/Brand */}
        <div className="flex items-center gap-3">
          <div className="flex gap-1">
            <div className="h-6 w-0.5 bg-gradient-to-b from-blue-500 to-blue-600 rounded-full" />
            <div className="h-6 w-0.5 bg-gradient-to-b from-blue-500 to-blue-600 rounded-full opacity-70" />
            <div className="h-6 w-0.5 bg-gradient-to-b from-blue-500 to-blue-600 rounded-full opacity-40" />
          </div>
          <span className="text-lg font-semibold text-white">
            {organizationName || ""}
          </span>
        </div>

        {/* Right side controls */}
        <div className="flex items-center gap-4">
          {/* Notification Icon */}
          <button
            className="p-2 hover:bg-slate-800 rounded-lg transition-colors text-slate-400 hover:text-slate-300 relative"
            onClick={() => {
              setNotificationModalOpen(!isNotificationModalOpen); // Toggle modal
              markNotificationsAsRead(); // Mark notifications as read
            }}
          >
            <Bell size={20} />
            {unreadNotifications > 0 && (
              <span className="absolute top-0 right-0 bg-red-500 text-white text-xs font-bold rounded-full h-4 w-4 flex items-center justify-center">
                {console.log(
                  "Rendering Badge with Count:",
                  unreadNotifications
                )}
                {unreadNotifications}
              </span>
            )}
          </button>

          {/* User Menu */}
          <div className="relative">
            <button
              onClick={() => setShowDropdown(!showDropdown)}
              className="flex items-center gap-3 p-2 hover:bg-slate-800 rounded-lg transition-colors"
            >
              <div className="w-8 h-8 bg-slate-700 rounded-full flex items-center justify-center">
                <User size={20} className="text-slate-300" />
              </div>
              <span className="text-slate-300 text-sm hidden md:block">
                {user?.email}
              </span>
            </button>

            {/* Dropdown Menu */}
            {showDropdown && (
              <div className="absolute right-0 top-full mt-2 w-48 py-2 bg-slate-900 rounded-lg border border-slate-700 shadow-xl">
                <button
                  onClick={handleLogout}
                  className="w-full px-4 py-2 text-left text-red-400 hover:bg-slate-800 flex items-center gap-2"
                >
                  <LogOut size={16} />
                  <span>Logout</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Notification Modal */}
      {isNotificationModalOpen && (
        <div
          className="fixed inset-0 z-[10000] h-screen w-screen grid place-items-center bg-black bg-opacity-50 backdrop-blur-sm"
          onClick={() => setNotificationModalOpen(false)}
        >
          <div
            className="bg-slate-900 rounded-lg shadow-xl border border-slate-700 p-6 w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-lg font-bold text-white mb-4">Notifications</h2>
            {notifications.length > 0 ? (
              <ul className="space-y-3">
                {console.log(
                  "Rendering Notifications in Modal:",
                  notifications
                )}
                {notifications.map((notification) => (
                  <li
                    key={notification.id}
                    className="bg-slate-800 p-4 rounded-lg text-white"
                  >
                    <p className="font-medium">{notification.subject}</p>
                    <p className="text-sm text-slate-400">
                      {notification.note}
                    </p>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-slate-400">
                {console.log("No notifications to display")}
                No new notifications.
              </p>
            )}

            <button
              className="mt-4 w-full py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition"
              onClick={() => setNotificationModalOpen(false)}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </nav>
  );
};

export default ResidentNav;
